import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type FC, useCallback, useMemo, useState } from 'react';

import type { BusinessUnitConversationTaggingConfig, KanbanStatus } from '../../../services/types/businessUnit';
import type { ExtendedLead } from '../../../services/types/lead';
import { LIGHT_GREY, NAVY, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST, YELLOW } from '../../../styles/tokens';
import { KANBAN_COLUMN_TOOLTIPS } from '../data/kanbanColumnTooltips';
import ChatPanel from './ChatPanel';
import CollapsedColumnComponent from './KanbanCollapsedColumn';
import LeadCard, { isChatPendingFromSalesman } from './LeadCard';
import { LeadCardSkeletonGroup } from './LeadCardSkeleton';
import VirtualizedKanbanColumn, { type KanbanColumnConfig } from './VirtualizedKanbanColumn';

const ALL_COLUMN_CONFIGS: Record<string, KanbanColumnConfig> = {
  CREATED: {
    label: 'CREADOS',
    color: LIGHT_GREY,
    textColor: NAVY,
    tooltip: KANBAN_COLUMN_TOOLTIPS.CREATED,
  },
  HANDLED_BY_AI: {
    label: 'ATENDIDO IA',
    color: '#C2EBF4',
    textColor: NAVY,
    tooltip: KANBAN_COLUMN_TOOLTIPS.HANDLED_BY_AI,
  },
  UPLOADED_TO_CRM: {
    label: 'INTERESADO',
    color: YELLOW,
    textColor: NAVY,
    tooltip: KANBAN_COLUMN_TOOLTIPS.UPLOADED_TO_CRM,
  },
  PENDING_TO_BE_HANDLED: {
    label: 'PENDIENTE',
    color: '#42ABC3',
    textColor: '#ffffff',
    tooltip: KANBAN_COLUMN_TOOLTIPS.PENDING_TO_BE_HANDLED,
  },
  PENDING_TO_BE_ASSIGNED: {
    label: 'POR ASIGNAR',
    color: '#42ABC3',
    textColor: '#ffffff',
    tooltip: KANBAN_COLUMN_TOOLTIPS.PENDING_TO_BE_ASSIGNED,
  },
  ASSIGNED_TO_SALESMAN: {
    label: 'ASIGNADO',
    color: '#6DA6E0',
    textColor: NAVY,
    tooltip: KANBAN_COLUMN_TOOLTIPS.ASSIGNED_TO_SALESMAN,
  },
  HANDLED_BY_SALESMAN: {
    label: 'ATENDIDO',
    color: '#2C5889',
    textColor: '#ffffff',
    tooltip: KANBAN_COLUMN_TOOLTIPS.HANDLED_BY_SALESMAN,
  },
  CLOSED: {
    label: 'CERRADO',
    color: NAVY,
    textColor: '#ffffff',
    tooltip: KANBAN_COLUMN_TOOLTIPS.CLOSED,
  },
};

const DEFAULT_STATUS_CONFIG: KanbanStatus[] = ['CREATED', 'HANDLED_BY_AI', 'UPLOADED_TO_CRM', 'CLOSED'];

const PENDING_CHAT_COLUMNS: ReadonlySet<string> = new Set([
  'PENDING_TO_BE_HANDLED',
  'PENDING_TO_BE_ASSIGNED',
  'ASSIGNED_TO_SALESMAN',
  'HANDLED_BY_SALESMAN',
]);

const KanbanContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  height: '100%',
  overflow: 'auto',
  padding: theme.spacing(1),
  position: 'relative',
}));

interface KanbanBoardProps {
  leadsByStatus: Record<string, ExtendedLead[]>;
  statusCounts: Record<string, number>;
  paginationInfo: Record<
    string,
    {
      page: number;
      hasMore: boolean;
      totalCount: number;
    }
  >;
  loadingStates: Record<
    string,
    {
      isLoadingMore: boolean;
      isRefreshing: boolean;
    }
  >;
  isInitialLoading: boolean;
  businessUnitId: number | null;
  statusConfigProp?: KanbanStatus[] | null;
  conversationTaggingConfig?: BusinessUnitConversationTaggingConfig | null;
  onLoadMore: (status: string) => void;
  getSkeletonCount: (status: string) => number;
}

const KanbanBoard: FC<KanbanBoardProps> = ({
  leadsByStatus,
  statusCounts,
  paginationInfo,
  loadingStates,
  isInitialLoading,
  onLoadMore,
  getSkeletonCount,
  businessUnitId,
  statusConfigProp,
  conversationTaggingConfig,
}) => {
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>(['CLOSED']);
  const [chatClientId, setChatClientId] = useState<number | null>(null);

  const activeStatuses = statusConfigProp ?? DEFAULT_STATUS_CONFIG;

  const visibleColumns = useMemo(() => {
    const columns: Record<string, KanbanColumnConfig> = {};
    for (const status of activeStatuses) {
      if (ALL_COLUMN_CONFIGS[status]) {
        columns[status] = ALL_COLUMN_CONFIGS[status];
      }
    }
    return columns;
  }, [activeStatuses]);

  const mergedLeadsByStatus = useMemo(() => {
    const merged = { ...leadsByStatus };
    const interestingLeads = merged.INTERESTING_FOR_SALESMAN || [];
    if (interestingLeads.length > 0) {
      const aiLeads = merged.HANDLED_BY_AI || [];
      const existingIds = new Set(aiLeads.map((l) => l.id));
      const uniqueInteresting = interestingLeads.filter((l) => !existingIds.has(l.id));
      merged.HANDLED_BY_AI = [...aiLeads, ...uniqueInteresting];
    }
    return merged;
  }, [leadsByStatus]);

  const mergedStatusCounts = useMemo(() => {
    const merged = { ...statusCounts };
    const interestingCount = merged.INTERESTING_FOR_SALESMAN || 0;
    if (interestingCount > 0) {
      merged.HANDLED_BY_AI = (merged.HANDLED_BY_AI || 0) + interestingCount;
    }
    return merged;
  }, [statusCounts]);

  const pendingChatCountsByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const status of PENDING_CHAT_COLUMNS) {
      const leads = mergedLeadsByStatus[status] || [];
      counts[status] = leads.filter(isChatPendingFromSalesman).length;
    }
    return counts;
  }, [mergedLeadsByStatus]);

  const handleColumnCollapse = useCallback((status: string) => {
    setCollapsedColumns((prev) => {
      const isCurrentlyCollapsed = prev.includes(status);
      return isCurrentlyCollapsed ? prev.filter((col) => col !== status) : [...prev, status];
    });
  }, []);

  const handleCardClick = useCallback((clientId: number) => {
    if (!clientId) return;
    setChatClientId(clientId);
  }, []);

  const handleCloseChat = useCallback(() => {
    setChatClientId(null);
  }, []);

  const renderLeadCard = useCallback(
    (lead: ExtendedLead) => {
      return (
        <LeadCard
          lead={lead}
          onCardClick={handleCardClick}
          showPurchasedIcon={!!lead.purchased}
          businessUnitId={businessUnitId}
          conversationTaggingConfig={conversationTaggingConfig}
        />
      );
    },
    [businessUnitId, handleCardClick, conversationTaggingConfig]
  );

  if (isInitialLoading) {
    return (
      <KanbanContainer>
        {Object.entries(visibleColumns).map(([status, config]) => {
          const isCollapsed = collapsedColumns.includes(status);
          return (
            <Box
              key={status}
              sx={{
                flex: isCollapsed ? '0 0 48px' : '1 1 300px',
                minWidth: isCollapsed ? '48px' : '280px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: SURFACE_CONTAINER_LOW,
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleColumnCollapse(status);
                }}
                sx={{
                  p: isCollapsed ? 1 : '12px 16px',
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  borderTop: `3px solid ${config.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: isCollapsed ? 'column' : 'row',
                  cursor: isCollapsed ? 'pointer' : 'default',
                  minHeight: isCollapsed ? '100%' : 'auto',
                }}
              >
                {!isCollapsed && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: config.color,
                        }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Jost' }}>
                        {config.label}
                      </Typography>
                    </Box>
                    <CircularProgress size={18} sx={{ color: config.color }} />
                  </>
                )}
                {isCollapsed && (
                  <>
                    <CircularProgress size={16} sx={{ color: config.color, mt: 1.5 }} />
                    <Typography
                      sx={{
                        transform: 'rotate(180deg)',
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        fontFamily: 'Jost',
                        color: '#15183A',
                      }}
                    >
                      {config.label}
                    </Typography>
                    <Box />
                  </>
                )}
              </Box>

              {!isCollapsed && (
                <Box sx={{ p: 1, flex: 1 }}>
                  <LeadCardSkeletonGroup count={6} delay={100} />
                </Box>
              )}
            </Box>
          );
        })}
      </KanbanContainer>
    );
  }

  return (
    <>
      <KanbanContainer>
        {Object.entries(visibleColumns).map(([status, config]) => {
          const isCollapsed = collapsedColumns.includes(status);
          const leads = mergedLeadsByStatus[status] || [];
          const hasStatusCount = typeof mergedStatusCounts[status] !== 'undefined';
          const totalCount = hasStatusCount ? mergedStatusCounts[status] : 0;
          const pagination = paginationInfo[status] || { page: 0, hasMore: false, totalCount: 0 };
          const loading = loadingStates[status] || { isLoadingMore: false, isRefreshing: false };

          if (isCollapsed) {
            return (
              <CollapsedColumnComponent
                key={status}
                status={status}
                config={config}
                totalCount={totalCount}
                pendingChatCount={pendingChatCountsByStatus[status]}
                statusCountLoading={typeof mergedStatusCounts[status] === 'undefined'}
                onToggleCollapse={handleColumnCollapse}
              />
            );
          }

          return (
            <VirtualizedKanbanColumn
              key={status}
              config={config}
              leads={leads}
              totalCount={totalCount}
              pendingChatCount={pendingChatCountsByStatus[status]}
              hasMore={pagination.hasMore}
              isLoadingMore={loading.isLoadingMore}
              isRefreshing={loading.isRefreshing}
              skeletonCount={getSkeletonCount(status)}
              onLoadMore={() => onLoadMore(status)}
              renderLeadCard={renderLeadCard}
              statusCountLoading={typeof mergedStatusCounts[status] === 'undefined'}
              onToggleCollapse={() => handleColumnCollapse(status)}
            />
          );
        })}
      </KanbanContainer>

      {chatClientId && <ChatPanel clientId={chatClientId} onClose={handleCloseChat} />}
    </>
  );
};

export default KanbanBoard;
